// ── /llm/chat SSE 协议层（单一事实源）──
// 从 ChatInputBar 抽出的 reader 循环：分片缓冲、事件解析、2 分钟闲置看门狗。
// ChatInputBar（全局聊天栏）与 ChatPanel（工作台右栏）共用；编排（会话/模型/
// agent 开关/断连自愈）留在各消费方，本模块只管协议。
// 事件口径（与后端一致）：chunk / replace / sources / factcheck / error /
// tool_call / tool_result / tool_confirm_request / tool_confirm_result / agent_trace。

export interface ChatStreamHandlers {
  /** 增量正文：fullText=累计全文，delta=本帧增量 */
  onChunk?: (fullText: string, delta: string) => void;
  /** Grounding 重答：整体替换已流出的第一答（后端落库以重答为准） */
  onReplace?: (fullText: string) => void;
  /** 引用源事件（仅当引用非空时下发） */
  onSources?: (sources: unknown[]) => void;
  /** 规则版 FactCheck：invalid_refs 越界引用清单（空=全部合法） */
  onFactcheck?: (invalidRefs: unknown[]) => void;
  /** 流内错误事件（HTTP 200 下发，如云端 402「余额不足」） */
  onStreamError?: (message: string) => void;
  /** 并发闸排队公告（十九批 P0-2）：请求排过队（>1.5s）时流首下发一次，
   * 消费方应显示「排队中」占位而不是无限转圈 */
  onQueued?: () => void;
  // ── agent 环工具轨迹（非 agent 轮次不会出现）──
  onToolCall?: (data: { round?: number; name: string; summary?: string }) => void;
  onToolResult?: (data: { name: string; ok?: boolean }) => void;
  onToolConfirmRequest?: (data: { pending_id: string; name: string; summary?: string }) => void;
  onToolConfirmResult?: (data: { approved?: boolean }) => void;
  onAgentTrace?: (data: { trace?: unknown[]; rounds?: number }) => void;
}

export interface ChatStreamResult {
  /** 最终全文（replace 事件已应用） */
  fullContent: string;
  /** 流内错误消息（无则空串） */
  streamError: string;
  /** 全程是否收到过非空引用源 */
  receivedSources: boolean;
  /** factcheck 越界引用数 */
  factcheckInvalidCount: number;
}

const WATCHDOG_MS = 120_000; // 2 分钟无数据视为挂起（SSE 半开防护）

/**
 * 消费 /llm/chat 的 SSE 响应体。response.body 为空时直接返回全空结果。
 * 看门狗超时/读取异常会向上抛出（消费方自己的 catch 处理断连自愈/错误映射）。
 */
export async function consumeChatStream(
  response: Response,
  handlers: ChatStreamHandlers,
): Promise<ChatStreamResult> {
  const reader = response.body?.getReader();
  const result: ChatStreamResult = {
    fullContent: '', streamError: '', receivedSources: false, factcheckInvalidCount: 0,
  };
  if (!reader) return result;

  const decoder = new TextDecoder();
  let done = false;
  let buffer = '';
  while (!done) {
    // 闲置看门狗：SSE 半开（休眠/断网但连接未断）时 read() 永不 resolve——
    // 2 分钟无数据取消读取并抛错，消费方 finally 复位状态
    let watchdog: ReturnType<typeof setTimeout> | undefined;
    const { done: rd, value } = await Promise.race([
      reader.read(),
      new Promise<never>((_, reject) => {
        watchdog = setTimeout(() => {
          reader.cancel().catch(() => {});
          reject(new Error('模型响应超时（2 分钟无数据），请重试'));
        }, WATCHDOG_MS);
      }),
    ]);
    clearTimeout(watchdog); // 每次 read 成功即清理，防定时器泄漏
    done = rd;
    if (rd) break;

    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop() || '';

    for (const line of events) {
      if (!line.startsWith('data: ')) continue;
      let data: any;
      try {
        data = JSON.parse(line.slice(6));
      } catch {
        continue; // 半帧 JSON 忽略，下一帧补齐
      }
      if (data.type === 'chunk' && data.content) {
        result.fullContent += data.content;
        handlers.onChunk?.(result.fullContent, data.content);
      } else if (data.type === 'replace' && typeof data.content === 'string') {
        result.fullContent = data.content;
        handlers.onReplace?.(result.fullContent);
      } else if (data.type === 'sources') {
        if (Array.isArray(data.sources) && data.sources.length > 0) {
          result.receivedSources = true;
          handlers.onSources?.(data.sources);
        }
      } else if (data.type === 'factcheck') {
        if (Array.isArray(data.invalid_refs)) {
          result.factcheckInvalidCount = data.invalid_refs.length;
          handlers.onFactcheck?.(data.invalid_refs);
        }
      } else if (data.type === 'error' && data.message) {
        result.streamError = data.message;
        handlers.onStreamError?.(data.message);
      } else if (data.type === 'queued') {
        handlers.onQueued?.();
      } else if (data.type === 'tool_call') {
        handlers.onToolCall?.(data);
      } else if (data.type === 'tool_result') {
        handlers.onToolResult?.(data);
      } else if (data.type === 'tool_confirm_request') {
        handlers.onToolConfirmRequest?.(data);
      } else if (data.type === 'tool_confirm_result') {
        handlers.onToolConfirmResult?.(data);
      } else if (data.type === 'agent_trace') {
        handlers.onAgentTrace?.(data);
      }
    }
  }
  return result;
}
